import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export interface ChargingConnection {
  type: string;
  powerKw: number | null;
  quantity: number;
  fast: boolean;
}

export interface ChargingStation {
  id: number;
  name: string;
  lat: number;
  lng: number;
  address: string | null;
  town: string | null;
  operator: string | null;
  numPoints: number;
  fastCharge: boolean;
  maxPowerKw: number | null;
  status: string | null;
  isOperational: boolean | null;
  connections: ChargingConnection[];
  /** Pristext från OpenChargeMap (fri text, finns sällan) */
  usageCost: string | null;
  /** Hur stationen används, t.ex. publik/medlemskap krävs */
  usageType: string | null;
  payAtLocation: boolean | null;
  membershipRequired: boolean | null;
  operatorUrl: string | null;
}

interface OcmPoi {
  ID: number;
  AddressInfo?: {
    Title?: string;
    AddressLine1?: string;
    Town?: string;
    Latitude?: number;
    Longitude?: number;
  };
  OperatorInfo?: { Title?: string; WebsiteURL?: string | null } | null;
  UsageCost?: string | null;
  UsageType?: { Title?: string; IsPayAtLocation?: boolean | null; IsMembershipRequired?: boolean | null } | null;
  StatusType?: { Title?: string; IsOperational?: boolean | null } | null;
  NumberOfPoints?: number | null;
  Connections?:
    | {
        ConnectionType?: { Title?: string } | null;
        PowerKW?: number | null;
        Quantity?: number | null;
        Level?: { IsFastChargeCapable?: boolean } | null;
      }[]
    | null;
}

const bboxSchema = z.object({
  south: z.number().min(-90).max(90),
  west: z.number().min(-180).max(180),
  north: z.number().min(-90).max(90),
  east: z.number().min(-180).max(180),
  countries: z.array(z.enum(["SE", "NO", "FI", "DK"])).min(1).max(4).default(["SE"]),
});

/**
 * Hämtar laddstationer inom kartans utsnitt från OpenChargeMap.
 * API-nyckeln läses server-side och exponeras aldrig för klienten.
 */
export const getChargingStations = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => bboxSchema.parse(d))
  .handler(async ({ data }): Promise<{ stations: ChargingStation[] }> => {
    const apiKey = process.env["OPENCHARGEMAP_API_KEY"];
    if (!apiKey) throw new Error("OPENCHARGEMAP_API_KEY saknas");

    // Ogiltigt eller omvänt utsnitt (t.ex. karta utanför Norden) – svara tomt.
    if (data.south > data.north || data.west > data.east) return { stations: [] };

    const bbox = `(${data.north},${data.west}),(${data.south},${data.east})`;
    const url =
      "https://api.openchargemap.io/v3/poi/?output=json&maxresults=5000" +
      "&compact=false&verbose=false&countrycode=" +
      data.countries.join(",") +
      "&boundingbox=" +
      encodeURIComponent(bbox) +
      "&key=" +
      encodeURIComponent(apiKey);

    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`OpenChargeMap svarade ${res.status}`);
    const raw = (await res.json()) as OcmPoi[];

    const stations: ChargingStation[] = (Array.isArray(raw) ? raw : [])
      .filter((p) => p.AddressInfo?.Latitude != null && p.AddressInfo?.Longitude != null)
      .map((p) => {
        const conns = (p.Connections ?? []).map((c) => ({
          type: c.ConnectionType?.Title ?? "Okänd kontakt",
          powerKw: c.PowerKW ?? null,
          quantity: c.Quantity ?? 1,
          fast: !!c.Level?.IsFastChargeCapable || (c.PowerKW ?? 0) >= 43,
        }));
        const powers = conns.map((c) => c.powerKw ?? 0);
        const max = powers.length ? Math.max(...powers) : 0;
        return {
          id: p.ID,
          name: p.AddressInfo?.Title ?? "Laddstation",
          lat: p.AddressInfo!.Latitude!,
          lng: p.AddressInfo!.Longitude!,
          address: p.AddressInfo?.AddressLine1 ?? null,
          town: p.AddressInfo?.Town ?? null,
          operator: p.OperatorInfo?.Title ?? null,
          numPoints: p.NumberOfPoints ?? Math.max(1, conns.reduce((a, c) => a + c.quantity, 0)),
          fastCharge: conns.some((c) => c.fast),
          maxPowerKw: max > 0 ? max : null,
          status: p.StatusType?.Title ?? null,
          isOperational: p.StatusType?.IsOperational ?? null,
          connections: conns,
          usageCost: p.UsageCost?.trim() || null,
          usageType: p.UsageType?.Title ?? null,
          payAtLocation: p.UsageType?.IsPayAtLocation ?? null,
          membershipRequired: p.UsageType?.IsMembershipRequired ?? null,
          operatorUrl: /^https?:\/\//i.test(p.OperatorInfo?.WebsiteURL ?? "") ? p.OperatorInfo!.WebsiteURL! : null,
        };
      });

    return { stations };
  });

const geocodeSchema = z.object({
  query: z.string().trim().min(2).max(120),
});

export interface PlaceSuggestion {
  /** Kort etikett, t.ex. "Örebro" eller "Storgatan 1". */
  label: string;
  /** Kontext, t.ex. "Örebro län" eller "702 10 · Örebro". */
  detail: string;
  kind: "city" | "postcode" | "address" | "street" | "place";
  lat: number;
  lng: number;
}

/**
 * Geokodar adress, postnummer eller ort via OpenStreetMap Nominatim (nordiska länder).
 * Används för sökrutans autocomplete på laddstationskartan. Fri text täcker alla tre typerna;
 * addressdetails=1 ger tydliga korta etiketter med typ, och vid postnummerliknande sökning
 * kompletteras med en strukturerad postalcodesökning för bättre träffar.
 */
export const geocodePlace = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => geocodeSchema.parse(d))
  .handler(async ({ data }): Promise<{ suggestions: PlaceSuggestion[] }> => {
    const headers = { Accept: "application/json", "User-Agent": "Elmarknad.se laddstationskarta" };
    const q = data.query;
    const base =
      "https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&addressdetails=1" +
      "&countrycodes=se,no,fi,dk&accept-language=sv";

    const urls: string[] = [];
    // Svenskt postnummer (t.ex. "702 10" eller "70210"): strukturerad svensk sökning först,
    // annars kan fritext träffa samma nummer i Finland/Norge och fylla hela listan.
    const pc = q.replace(/\s/g, "");
    if (/^\d{5}$/.test(pc)) {
      urls.push(`${base}&postalcode=${encodeURIComponent(pc)}&country=se`);
    }
    urls.push(`${base}&q=${encodeURIComponent(q)}`);

    type NomResult = {
      display_name?: string;
      name?: string;
      addresstype?: string;
      lat?: string;
      lon?: string;
      address?: {
        road?: string;
        house_number?: string;
        postcode?: string;
        city?: string;
        town?: string;
        village?: string;
        municipality?: string;
        county?: string;
        state?: string;
      };
    };

    const kindOf = (t?: string): PlaceSuggestion["kind"] => {
      if (t === "postcode") return "postcode";
      if (t && ["city", "town", "village", "municipality", "county"].includes(t)) return "city";
      if (t === "road") return "street";
      if (t === "house" || t === "building") return "address";
      return "place";
    };

    const seen = new Set<string>();
    const suggestions: PlaceSuggestion[] = [];
    for (const url of urls) {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Geokodning svarade ${res.status}`);
      const raw = (await res.json()) as NomResult[];
      for (const r of Array.isArray(raw) ? raw : []) {
        if (r.lat == null || r.lon == null || !r.display_name) continue;
        const lat = Number(r.lat);
        const lng = Number(r.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const key = `${lat.toFixed(4)},${lng.toFixed(4)}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const a = r.address ?? {};
        const road = a.road?.trim();
        const hn = a.house_number?.trim();
        // Adresser visas som "Gata nr" – aldrig bara husnumret
        const label = (road && hn ? `${road} ${hn}` : road && (r.addresstype === "road" || !r.name?.trim()) ? road
          : r.name?.trim() || r.display_name.split(",")[0]?.trim() || r.display_name).slice(0, 80);
        const kind: PlaceSuggestion["kind"] = road && hn ? "address" : r.addresstype === "road" ? "street" : kindOf(r.addresstype);
        const detailParts = [a.postcode, a.city ?? a.town ?? a.village ?? a.municipality, a.county ?? a.state]
          .filter((x): x is string => !!x && x.trim() !== "" && x.trim() !== label)
          .slice(0, 3);
        const detail = detailParts.length
          ? detailParts.join(" · ")
          : r.display_name.split(",").slice(1).join(",").trim().slice(0, 120);

        suggestions.push({ label, detail, kind, lat, lng });
        if (suggestions.length >= 6) break;
      }
      if (suggestions.length >= 6) break;
    }
    return { suggestions };
  });

const reverseSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export interface ReverseGeocodeResult {
  /** Kort etikett, t.ex. "Kocksgatan 5" eller "Örebro". */
  label: string;
  /** Kontext, t.ex. "702 10 · Örebro" eller "Örebro län". */
  detail: string | null;
  kind: "address" | "street" | "city" | "place";
}

/**
 * Bakåtgeokodar en position (kartklick eller flyttad pin) till närmaste adress
 * via OpenStreetMap Nominatim. Används för att visa utgångspunkten som
 * listan sorteras efter.
 */
export const reverseGeocode = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => reverseSchema.parse(d))
  .handler(async ({ data }): Promise<ReverseGeocodeResult> => {
    const headers = { Accept: "application/json", "User-Agent": "Elmarknad.se laddstationskarta" };
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${data.lat}&lon=${data.lng}` +
      "&zoom=18&addressdetails=1&accept-language=sv";
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Bakåtgeokodning svarade ${res.status}`);
    const r = (await res.json()) as {
      addresstype?: string;
      address?: {
        road?: string;
        house_number?: string;
        postcode?: string;
        city?: string;
        town?: string;
        village?: string;
        municipality?: string;
        county?: string;
        state?: string;
      };
    };
    const a = r.address ?? {};
    const road = a.road?.trim();
    const hn = a.house_number?.trim();
    const city = a.city ?? a.town ?? a.village ?? a.municipality ?? "";
    const region = a.county ?? a.state ?? "";
    if (road) {
      const label = hn ? `${road} ${hn}` : road;
      const detail = [a.postcode, city].filter((x): x is string => !!x && x.trim() !== "").join(" · ") || null;
      return { label: label.slice(0, 80), detail, kind: hn ? "address" : "street" };
    }
    const label = (city || region || "Din position").slice(0, 80);
    const detail = city && region && region !== city ? region : null;
    return { label, detail, kind: city ? "city" : "place" };
  });

const reportSchema = z.object({
  stationId: z.number().int().positive(),
  stationName: z.string().max(300).optional(),
  issueType: z.enum(["out_of_order", "wrong_address", "wrong_connectors", "other"]),
  description: z.string().trim().max(1000).optional(),
  website: z.string().max(0).optional(), // honeypot
});

export const submitStationReport = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => reportSchema.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("charging_station_reports").insert({
      station_id: data.stationId,
      station_name: data.stationName ?? null,
      issue_type: data.issueType,
      description: data.description || null,
    });
    if (error) throw new Error("Rapporten kunde inte sparas");
    return { ok: true };
  });

export interface MunicipalityChargingStats {
  radiusKm: number;
  stations: number;
  points: number;
  fastStations: number;
  fastPoints: number;
  maxPowerKw: number | null;
  operators: { name: string; stations: number }[];
  connectorTypes: { type: string; count: number }[];
  powerBands: { label: string; count: number }[];
}

const municipalityStatsSchema = z.object({
  lat: z.number().min(54).max(70),
  lng: z.number().min(10).max(25),
  radiusKm: z.number().min(2).max(60),
});

/** Nyckeltal om laddinfrastrukturen runt en kommuns centralort. */
export const getMunicipalityChargingStats = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => municipalityStatsSchema.parse(d))
  .handler(async ({ data }): Promise<MunicipalityChargingStats> => {
    const dLat = data.radiusKm / 111;
    const dLng = data.radiusKm / (111 * Math.cos((data.lat * Math.PI) / 180));
    const { stations: all } = await getChargingStations({
      data: {
        south: data.lat - dLat,
        north: data.lat + dLat,
        west: data.lng - dLng,
        east: data.lng + dLng,
        countries: ["SE"],
      },
    });
    const toRad = (v: number) => (v * Math.PI) / 180;
    const within = all.filter((s) => {
      const a =
        Math.sin(toRad(s.lat - data.lat) / 2) ** 2 +
        Math.cos(toRad(data.lat)) * Math.cos(toRad(s.lat)) * Math.sin(toRad(s.lng - data.lng) / 2) ** 2;
      return 2 * 6371 * Math.asin(Math.sqrt(a)) <= data.radiusKm;
    });

    const ops = new Map<string, number>();
    const types = new Map<string, number>();
    const bands = { "Under 11 kW": 0, "11–22 kW": 0, "23–99 kW": 0, "100 kW eller mer": 0 };
    let points = 0;
    let fastPoints = 0;
    let max = 0;
    for (const s of within) {
      points += s.numPoints;
      const op = s.operator && !/unknown|okänd/i.test(s.operator) ? s.operator : "Okänd operatör";
      ops.set(op, (ops.get(op) ?? 0) + 1);
      for (const c of s.connections) {
        types.set(c.type, (types.get(c.type) ?? 0) + c.quantity);
        if (c.fast) fastPoints += c.quantity;
        const p = c.powerKw ?? 0;
        max = Math.max(max, p);
        if (p >= 100) bands["100 kW eller mer"] += c.quantity;
        else if (p >= 23) bands["23–99 kW"] += c.quantity;
        else if (p >= 11) bands["11–22 kW"] += c.quantity;
        else if (p > 0) bands["Under 11 kW"] += c.quantity;
      }
    }
    const top = <T,>(m: Map<string, number>, n: number, f: (k: string, v: number) => T) =>
      [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([k, v]) => f(k, v));

    return {
      radiusKm: data.radiusKm,
      stations: within.length,
      points,
      fastStations: within.filter((s) => s.fastCharge).length,
      fastPoints,
      maxPowerKw: max > 0 ? max : null,
      operators: top(new Map([...ops].filter(([k]) => k !== "Okänd operatör")), 6, (name, stations) => ({ name, stations })),
      connectorTypes: top(new Map([...types].filter(([k]) => !/^unknown|okänd/i.test(k))), 5, (type, count) => ({ type, count })),
      powerBands: Object.entries(bands).map(([label, count]) => ({ label, count })),
    };
  });
